import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import { CampaignPlanAllocationMetricId, CampaignPlanConfig } from 'components/common/types/CampaignPlan.types';
import { changeDirectSalesData } from 'store/dealManagement/reducer';
import { AllocationMetricColumn, GROUP_BY_OPTION_CODES } from './ShareCampaignPlanModal.types';

type UseShareCampaignPlanModalStateParams = {
  initialCampaignPlanConfig: CampaignPlanConfig;
  isConfigLoading: boolean;
  shareUrl: string;
  onClose: () => void;
  onGenerateLink: (campaignPlanConfig: CampaignPlanConfig) => Promise<void>;
  onUpdateLink: (campaignPlanConfig: CampaignPlanConfig, hasConfigChanges: boolean) => Promise<boolean>;
};

type UseShareCampaignPlanModalStateReturn = {
  campaignPlanConfig: CampaignPlanConfig;
  hasConfigChanges: boolean;
  isDiscardModalOpen: boolean;
  setIsDiscardModalOpen: (isOpen: boolean) => void;
  handleCloseModal: () => void;
  handleDiscardChanges: () => void;
  handleToggleMapChange: (isChecked: boolean) => void;
  handleTogglePoiChange: (isChecked: boolean) => void;
  handleGenerateLink: () => Promise<void>;
  handleUpdateLink: () => Promise<void>;
  handleMetricChange: (
    metricId: CampaignPlanAllocationMetricId,
    metricColumn: AllocationMetricColumn,
  ) => (isChecked: boolean) => void;
  isIncludeGroupBysEnabled: boolean;
  handleIncludeGroupBysChange: (isChecked: boolean) => void;
  handleGroupByChange: (groupBy: string) => void;
};

const normalizeCampaignPlanConfig = (config: CampaignPlanConfig): CampaignPlanConfig => ({
  ...config,
  allocationReport: {
    ...config.allocationReport,
    allowedGroupByOptions: config.allocationReport.allowedGroupByOptions ?? [...GROUP_BY_OPTION_CODES],
  },
});

export const useShareCampaignPlanModalState = ({
  initialCampaignPlanConfig,
  isConfigLoading,
  shareUrl,
  onClose,
  onGenerateLink,
  onUpdateLink,
}: UseShareCampaignPlanModalStateParams): UseShareCampaignPlanModalStateReturn => {
  const dispatch = useDispatch();
  const [campaignPlanConfig, setCampaignPlanConfig] = useState(() =>
    normalizeCampaignPlanConfig(initialCampaignPlanConfig),
  );
  const [savedCampaignPlanConfig, setSavedCampaignPlanConfig] = useState(() =>
    normalizeCampaignPlanConfig(initialCampaignPlanConfig),
  );
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isIncludeGroupBysEnabled, setIsIncludeGroupBysEnabled] = useState(
    () => normalizeCampaignPlanConfig(initialCampaignPlanConfig).allocationReport.allowedGroupByOptions.length > 0,
  );

  useEffect(() => {
    if (isConfigLoading) return;

    const normalizedConfig = normalizeCampaignPlanConfig(initialCampaignPlanConfig);

    setCampaignPlanConfig(normalizedConfig);
    setSavedCampaignPlanConfig(normalizedConfig);
    setIsIncludeGroupBysEnabled(normalizedConfig.allocationReport.allowedGroupByOptions.length > 0);
  }, [initialCampaignPlanConfig, isConfigLoading]);

  const hasConfigChanges = useMemo(
    () => !isEqual(savedCampaignPlanConfig, campaignPlanConfig),
    [savedCampaignPlanConfig, campaignPlanConfig],
  );

  useEffect(() => {
    dispatch(changeDirectSalesData({ hasUnsavedCampaignPlanConfigChanges: hasConfigChanges && !!shareUrl }));

    return () => {
      dispatch(changeDirectSalesData({ hasUnsavedCampaignPlanConfigChanges: false }));
    };
  }, [dispatch, hasConfigChanges, shareUrl]);

  const clearUnsavedConfigChanges = (): void => {
    dispatch(changeDirectSalesData({ hasUnsavedCampaignPlanConfigChanges: false }));
  };

  const handleCloseModal = (): void => {
    if (hasConfigChanges && shareUrl) {
      setIsDiscardModalOpen(true);
      return;
    }

    clearUnsavedConfigChanges();
    onClose();
  };

  const handleDiscardChanges = (): void => {
    setIsDiscardModalOpen(false);
    setCampaignPlanConfig(savedCampaignPlanConfig);
    setIsIncludeGroupBysEnabled(savedCampaignPlanConfig.allocationReport.allowedGroupByOptions.length > 0);
    clearUnsavedConfigChanges();
    onClose();
  };

  const handleToggleMapChange = (isChecked: boolean): void => {
    setCampaignPlanConfig((previous) => ({
      ...previous,
      map: {
        ...previous.map,
        includeMap: isChecked,
      },
    }));
  };

  const handleTogglePoiChange = (isChecked: boolean): void => {
    setCampaignPlanConfig((previous) => ({
      ...previous,
      map: {
        ...previous.map,
        includePOIs: isChecked,
      },
    }));
  };

  const handleGenerateLink = async (): Promise<void> => {
    await onGenerateLink(campaignPlanConfig);
  };

  const handleUpdateLink = async (): Promise<void> => {
    const isSaved = await onUpdateLink(campaignPlanConfig, hasConfigChanges);

    if (!isSaved) return;

    setSavedCampaignPlanConfig(campaignPlanConfig);
    clearUnsavedConfigChanges();
  };

  const handleMetricChange =
    (metricId: CampaignPlanAllocationMetricId, metricColumn: AllocationMetricColumn) =>
    (isChecked: boolean): void => {
      setCampaignPlanConfig((previous) => ({
        ...previous,
        allocationReport: {
          ...previous.allocationReport,
          [metricId]: {
            ...previous.allocationReport[metricId],
            [metricColumn]: isChecked,
          },
        },
      }));
    };

  const handleIncludeGroupBysChange = (isChecked: boolean): void => {
    setIsIncludeGroupBysEnabled(isChecked);

    setCampaignPlanConfig((previous) => ({
      ...previous,
      allocationReport: {
        ...previous.allocationReport,
        allowedGroupByOptions: isChecked ? [...GROUP_BY_OPTION_CODES] : [],
      },
    }));
  };

  const handleGroupByChange = (groupBy: string): void => {
    setCampaignPlanConfig((previous) => {
      const selectedGroupBy = previous.allocationReport.allowedGroupByOptions;
      const allowedGroupByOptions = selectedGroupBy.includes(groupBy)
        ? selectedGroupBy.filter((selected) => selected !== groupBy)
        : [...selectedGroupBy, groupBy];

      return {
        ...previous,
        allocationReport: {
          ...previous.allocationReport,
          allowedGroupByOptions,
        },
      };
    });
  };

  return {
    campaignPlanConfig,
    hasConfigChanges,
    isDiscardModalOpen,
    setIsDiscardModalOpen,
    handleCloseModal,
    handleDiscardChanges,
    handleToggleMapChange,
    handleTogglePoiChange,
    handleGenerateLink,
    handleUpdateLink,
    handleMetricChange,
    isIncludeGroupBysEnabled,
    handleIncludeGroupBysChange,
    handleGroupByChange,
  };
};
